import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import {
	getFolderHasChildrenInBranchLists,
	setFolderHasChildrenInBranchList,
} from "@src/components/board/queries/navbar-branch-has-children";
import { graphql } from "@src/gql/gql";
import { NavBarItemType, type NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const createFolderMutation = graphql(/* GraphQL */ `
	mutation createFolder(
		$section: NavbarSection!
		$name: String!
		$parentFolderKey: String
		$beforeKey: String
		$afterKey: String
	) {
		navbar {
			createFolder(
				section: $section
				name: $name
				parentFolderKey: $parentFolderKey
				beforeKey: $beforeKey
				afterKey: $afterKey
			) {
				key
				name
				parentKey
				rank
			}
		}
	}
`);

type CreateFolderInput = {
	section: NavbarSection;
	name: string;
	parentFolderKey?: string | null;
	beforeKey?: string | null;
	afterKey?: string | null;
};

export function useCreateFolder() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			name,
			parentFolderKey,
			beforeKey,
			afterKey,
		}: CreateFolderInput) => {
			const response = await client.mutate({
				mutation: createFolderMutation,
				variables: {
					section,
					name,
					parentFolderKey,
					beforeKey: beforeKey ?? undefined,
					afterKey: afterKey ?? undefined,
				},
			});
			return response.data?.navbar?.createFolder ?? null;
		},
		onMutate: async (variables) => {
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			await queryClient.cancelQueries({ queryKey });
			const previousItems =
				queryClient.getQueryData<NavbarBranchItem[]>(queryKey);

			const tempKey = `temp-folder-${Date.now()}`;
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				const placeholder: NavbarBranchItem = {
					key: tempKey,
					name: variables.name,
					type: NavBarItemType.Folder,
					parentKey: variables.parentFolderKey ?? null,
					rank: "",
					hasChildren: false,
				};
				return [placeholder, ...(old ?? [])];
			});

			let previousParentHadChildren: boolean | undefined;
			if (variables.parentFolderKey) {
				previousParentHadChildren = getFolderHasChildrenInBranchLists(
					queryClient,
					variables.section,
					variables.parentFolderKey,
				);
				setFolderHasChildrenInBranchList(
					queryClient,
					variables.section,
					variables.parentFolderKey,
					true,
				);
			}

			return { previousItems, queryKey, previousParentHadChildren, tempKey };
		},
		onSuccess: (data, variables, context) => {
			if (!data || !context?.tempKey) {
				return;
			}
			const queryKey = navbarBranchQueryKeys.branch(
				variables.section,
				variables.parentFolderKey,
			);
			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
				if (!old) {
					return old;
				}
				const realItem: NavbarBranchItem = {
					key: data.key,
					name: data.name,
					type: NavBarItemType.Folder,
					parentKey: data.parentKey ?? null,
					rank: data.rank,
					hasChildren: false,
				};
				return old.map((i) => (i.key === context.tempKey ? realItem : i));
			});
		},
		onError: (error, variables, context) => {
			if (context?.queryKey) {
				queryClient.setQueryData(context.queryKey, context.previousItems);
			}
			if (
				variables.parentFolderKey &&
				context?.previousParentHadChildren !== undefined
			) {
				setFolderHasChildrenInBranchList(
					queryClient,
					variables.section,
					variables.parentFolderKey,
					context.previousParentHadChildren,
				);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.branch(
					variables.section,
					variables.parentFolderKey,
				),
			});
		},
	});
}
